import type { StockImage } from "@/data/site/stockImages";

/**
 * Full-bleed hero background image with a heavy navy gradient overlay so
 * white text stays readable. Use inside a `relative` hero section as the
 * first child; place text content in a sibling with `relative z-10`.
 */
interface HeroBackgroundProps {
  image: StockImage;
  /** Tailwind object-position class, default "object-center". */
  position?: string;
  /** Render with eager loading + high fetchpriority (above the fold). */
  priority?: boolean;
  /** Overlay intensity: "strong" (default) for full-bleed heroes, "medium" for lighter sections. */
  intensity?: "strong" | "medium";
}

export const HeroBackground = ({
  image,
  position = "object-center",
  priority = true,
  intensity = "strong",
}: HeroBackgroundProps) => {
  // Build a responsive srcset from the same Unsplash photo id.
  const base = image.src.split("?")[0];
  const u = (w: number) =>
    `${base}?auto=format&fit=crop&q=70&w=${w}`;

  return (
    <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
      <img
        src={u(1600)}
        srcSet={`${u(800)} 800w, ${u(1200)} 1200w, ${u(1600)} 1600w, ${u(2000)} 2000w`}
        sizes="100vw"
        alt=""
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        fetchPriority={priority ? "high" : "auto"}
        className={`absolute inset-0 w-full h-full object-cover ${position}`}
       width={1600} height={900} />
      {/* Single combined overlay — replaces three stacked layers to cut
          mobile paint cost while preserving the navy wash + directional fade. */}
      <div
        className={
          intensity === "strong"
            ? "absolute inset-0 bg-gradient-to-r from-navy-dark via-navy-dark/90 to-navy-dark/70"
            : "absolute inset-0 bg-gradient-to-r from-navy-dark/95 via-navy-dark/80 to-navy-dark/55"
        }
      />
    </div>
  );
};
